import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import styled from "styled-components";

import { Spinner } from "../Spinner";
import {
  clampPlaybackSeekTime,
  useVideoTimeline,
  VideoSurface,
  type PlaybackTransport,
  type VideoHandle,
  type VideoStatus,
} from "../Video";
import { BUFFERING_INDICATOR_DELAY_MS, useDelayedFlag } from "../Video/useDelayedFlag";
import { AnnotationContourLayer } from "./AnnotationContourLayer";
import { AnnotationRenderResourceImage } from "./AnnotationRenderResourceImage";
import {
  AnnotationOverlay,
  AnnotationViewport,
  CapturedVideoSurface,
  EmptyRaster,
  Raster,
  ViewportOverlayActions,
  annotationStageProps,
} from "./AnnotationViewportPrimitives";
import {
  useAnnotationRasterStage,
  useVideoRaster,
  type RasterReporter,
} from "./annotationRasterStage";
import { annotationStudioText } from "./messages";
import { targetColorCss } from "./targetColor";
import type {
  AnnotationImageRenderResource,
  AnnotationMediaResultBinding,
  AnnotationStudioLocale,
  AnnotationTarget,
  RasterLayerAlignment,
  ReplayableRenderSourceDescriptor,
  TimedContourSample,
  TimedLayerTiming,
  TimedMediaLayer,
  TimedRasterSample,
  TimedRasterSource,
  TimedSampleSource,
} from "./types";

const PREFETCH_BUCKET_SECONDS = 1;
const PREFETCH_MARGIN_SECONDS = 0.25;
/** emit이 끊긴 비정상 구간에서 dead-reckoning 추정이 실제 클럭보다 폭주하지 않게 하는 한도. */
const MAX_DEAD_RECKONING_SECONDS = 0.5;

type LayerStatus = "idle" | "pending" | "error";
type RasterLayer = Extract<TimedMediaLayer, { kind: "raster" }>;
type SampledRasterLayer = RasterLayer & {
  readonly source: Extract<TimedRasterSource, { delivery: "samples" }>;
};
type VideoRasterLayer = RasterLayer & {
  readonly source: Extract<TimedRasterSource, { delivery: "video" }>;
};

const ResultStatus = styled.div<{ $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.45rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceOverlay};
  color: ${({ $error, theme }) => ($error ? theme.color.status.error : theme.color.text.default)};
  font-size: ${({ theme }) => theme.typography.size.caption};
  pointer-events: none;
`;

const RasterLayerFrame = styled.div<{
  $alignment: RasterLayerAlignment;
  $role: "mask" | "heatmap";
}>`
  position: absolute;
  top: ${({ $alignment }) => `${$alignment.y * 100}%`};
  left: ${({ $alignment }) => `${$alignment.x * 100}%`};
  width: ${({ $alignment }) => `${$alignment.width * 100}%`};
  height: ${({ $alignment }) => `${$alignment.height * 100}%`};
  opacity: ${({ $role }) => ($role === "mask" ? 0.5 : 0.6)};
  pointer-events: none;

  & > *,
  & img,
  & canvas {
    width: 100%;
    height: 100%;
  }
`;

const RasterVideoSurface = styled(VideoSurface)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  & > div {
    height: 100%;
    border: 0;
  }
`;

function clampNormalized(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : fallback;
}

function normalizeAlignment(alignment: RasterLayerAlignment): RasterLayerAlignment {
  const x = clampNormalized(alignment.x, 0);
  const y = clampNormalized(alignment.y, 0);
  return {
    x,
    y,
    width: Math.min(1 - x, clampNormalized(alignment.width, 1)),
    height: Math.min(1 - y, clampNormalized(alignment.height, 1)),
  };
}

function layerTime(masterTimeSeconds: number, timing: TimedLayerTiming): number {
  const offsetSeconds = Number.isFinite(timing.offsetSeconds) ? (timing.offsetSeconds ?? 0) : 0;
  return Math.max(0, masterTimeSeconds - offsetSeconds);
}

function useTimedSample<TSample>(
  source: TimedSampleSource<TSample>,
  timing: TimedLayerTiming,
  transport: PlaybackTransport,
): Readonly<{ sample: TSample | null; pending: boolean; error: boolean }> {
  const snapshotTimeSeconds = useVideoTimeline(
    transport,
    (snapshot) => snapshot.currentTimeSeconds,
  );
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const [, refresh] = useReducer((version: number) => version + 1, 0);
  const [pending, setPending] = useState(false);
  const [prefetchError, setPrefetchError] = useState(false);

  // Dead-reckoning: replay transport는 emit 간격(예: 100ms)으로만 스냅샷을 갱신하지만 base
  // video는 매 프레임 클럭을 따라간다. 재생 중에는 마지막 클럭 변화 이후 경과 × 배속만큼
  // 앞으로 추정한 시각으로 샘플을 조회해, 오버레이가 emit 주기만큼 영상 뒤에 붙지 않게 한다.
  // 리렌더는 rAF 루프가 조회 샘플이 실제로 바뀔 때만 일으킨다(§5-E — 프레임당 상태 갱신 금지).
  const baselineObservedAtRef = useRef(0);
  const estimateRef = useRef<number | null>(null);
  useEffect(() => {
    let lastTimeSeconds = transport.getSnapshot().currentTimeSeconds;
    let lastPlaying = transport.getSnapshot().playing;
    baselineObservedAtRef.current = performance.now();
    return transport.subscribe(() => {
      const snapshot = transport.getSnapshot();
      if (snapshot.currentTimeSeconds === lastTimeSeconds && snapshot.playing === lastPlaying) {
        return;
      }
      lastTimeSeconds = snapshot.currentTimeSeconds;
      lastPlaying = snapshot.playing;
      baselineObservedAtRef.current = performance.now();
      estimateRef.current = null;
    });
  }, [transport]);

  const masterTimeSeconds =
    playing && estimateRef.current !== null ? estimateRef.current : snapshotTimeSeconds;
  const localTimeSeconds = layerTime(masterTimeSeconds, timing);
  const query = {
    timeSeconds: localTimeSeconds,
    sampling: timing.sampling,
    ...(timing.offsetSeconds === undefined ? {} : { offsetSeconds: timing.offsetSeconds }),
    ...(timing.toleranceSeconds === undefined ? {} : { toleranceSeconds: timing.toleranceSeconds }),
  } as const;

  let sample: TSample | null = null;
  let lookupError = false;
  try {
    sample = source.getSampleAt(query);
  } catch {
    lookupError = true;
  }

  const renderedRef = useRef<{ sample: TSample | null; lookupError: boolean }>({
    sample: null,
    lookupError: false,
  });
  renderedRef.current = { sample, lookupError };

  const { sampling, offsetSeconds, toleranceSeconds } = timing;
  useEffect(() => {
    if (
      !playing ||
      typeof requestAnimationFrame !== "function" ||
      typeof cancelAnimationFrame !== "function"
    ) {
      return;
    }

    let frameId = 0;
    const tick = (frameNow: number) => {
      const snapshot = transport.getSnapshot();
      const elapsedSeconds = Math.max(0, (frameNow - baselineObservedAtRef.current) / 1000);
      const estimate = clampPlaybackSeekTime(
        snapshot,
        snapshot.currentTimeSeconds +
          Math.min(elapsedSeconds, MAX_DEAD_RECKONING_SECONDS) * snapshot.playbackRate,
      );
      estimateRef.current = estimate;

      let nextSample: TSample | null = null;
      let nextLookupError = false;
      try {
        nextSample = source.getSampleAt({
          timeSeconds: layerTime(estimate, { sampling, offsetSeconds, toleranceSeconds }),
          sampling,
          ...(offsetSeconds === undefined ? {} : { offsetSeconds }),
          ...(toleranceSeconds === undefined ? {} : { toleranceSeconds }),
        });
      } catch {
        nextLookupError = true;
      }
      const rendered = renderedRef.current;
      if (nextSample !== rendered.sample || nextLookupError !== rendered.lookupError) refresh();
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
      estimateRef.current = null;
    };
  }, [offsetSeconds, playing, sampling, source, toleranceSeconds, transport]);

  useEffect(() => source.subscribe(refresh), [source]);

  const prefetchBucket = Math.floor(localTimeSeconds / PREFETCH_BUCKET_SECONDS);
  const hasSample = sample !== null;
  useEffect(() => {
    if (hasSample || !source.prefetch) {
      setPending(false);
      if (hasSample) setPrefetchError(false);
      return;
    }

    const abortController = new AbortController();
    const startSeconds = Math.max(
      0,
      prefetchBucket * PREFETCH_BUCKET_SECONDS - PREFETCH_MARGIN_SECONDS,
    );
    const endSeconds = (prefetchBucket + 1) * PREFETCH_BUCKET_SECONDS + PREFETCH_MARGIN_SECONDS;
    setPrefetchError(false);
    try {
      const request = source.prefetch(
        { startSeconds, endSeconds },
        { signal: abortController.signal },
      );
      if (request) {
        setPending(true);
        void request.then(
          () => {
            if (!abortController.signal.aborted) {
              setPending(false);
              refresh();
            }
          },
          () => {
            if (!abortController.signal.aborted) {
              setPending(false);
              setPrefetchError(true);
            }
          },
        );
      } else {
        setPending(false);
      }
    } catch {
      setPending(false);
      setPrefetchError(true);
    }
    return () => abortController.abort();
  }, [hasSample, prefetchBucket, source]);

  return { sample, pending, error: lookupError || prefetchError };
}

function useRasterResource(
  descriptor: ReplayableRenderSourceDescriptor<AnnotationImageRenderResource> | null,
): Readonly<{
  resource: AnnotationImageRenderResource | null;
  pending: boolean;
  error: boolean;
}> {
  const [state, setState] = useState<{
    descriptor: ReplayableRenderSourceDescriptor<AnnotationImageRenderResource> | null;
    resource: AnnotationImageRenderResource | null;
    pending: boolean;
    error: boolean;
  }>({ descriptor: null, resource: null, pending: false, error: false });

  useEffect(() => {
    if (!descriptor) {
      setState({ descriptor: null, resource: null, pending: false, error: false });
      return;
    }
    const abortController = new AbortController();
    let acquired: AnnotationImageRenderResource | null = null;
    setState({ descriptor, resource: null, pending: true, error: false });
    void descriptor.acquire({ signal: abortController.signal }).then(
      (resource) => {
        if (abortController.signal.aborted) {
          resource.dispose();
          return;
        }
        acquired = resource;
        setState({ descriptor, resource, pending: false, error: false });
      },
      () => {
        if (!abortController.signal.aborted) {
          setState({ descriptor, resource: null, pending: false, error: true });
        }
      },
    );
    return () => {
      abortController.abort();
      acquired?.dispose();
    };
  }, [descriptor]);

  return state.descriptor === descriptor
    ? state
    : { resource: null, pending: descriptor !== null, error: false };
}

function ContourResultLayer({
  layer,
  transport,
  targetsById,
  onStatusChange,
}: {
  readonly layer: Extract<TimedMediaLayer, { kind: "contour" }>;
  readonly transport: PlaybackTransport;
  readonly targetsById: ReadonlyMap<string, AnnotationTarget>;
  readonly onStatusChange: (layerId: string, status: LayerStatus) => void;
}) {
  const { sample, pending, error } = useTimedSample<TimedContourSample>(
    layer.source,
    layer.timing,
    transport,
  );
  useEffect(() => {
    onStatusChange(layer.id, error ? "error" : pending ? "pending" : "idle");
  }, [error, layer.id, onStatusChange, pending]);

  return Object.values(sample?.targets ?? {}).map((result) => {
    const target = targetsById.get(result.targetId);
    return (
      <AnnotationContourLayer
        key={`${layer.id}:${result.targetId}`}
        contours={result.contours}
        presentation="tracking"
        data-media-result-layer={layer.id}
        data-media-result-target={result.targetId}
        style={
          {
            "--c6-annotation-target-color": target ? targetColorCss(target.color) : "currentColor",
          } as CSSProperties
        }
      />
    );
  });
}

function SampledRasterResultLayer({
  layer,
  transport,
  onStatusChange,
}: {
  readonly layer: SampledRasterLayer;
  readonly transport: PlaybackTransport;
  readonly onStatusChange: (layerId: string, status: LayerStatus) => void;
}) {
  const {
    sample,
    pending: samplePending,
    error: sampleError,
  } = useTimedSample<TimedRasterSample>(layer.source.samples, layer.timing, transport);
  const descriptor = sample?.frameSource ?? null;
  const {
    resource,
    pending: resourcePending,
    error: resourceError,
  } = useRasterResource(descriptor);
  const pending = samplePending || resourcePending;
  const error = sampleError || resourceError;

  useEffect(() => {
    onStatusChange(layer.id, error ? "error" : pending ? "pending" : "idle");
  }, [error, layer.id, onStatusChange, pending]);

  if (!resource) return null;
  return (
    <RasterLayerFrame
      $alignment={normalizeAlignment(layer.alignment)}
      $role={layer.role}
      data-media-result-layer={layer.id}
      data-media-result-raster={layer.role}
      data-raster-interpretation={layer.interpretation.type}
    >
      <AnnotationRenderResourceImage image={resource.image} kind="media-result" />
    </RasterLayerFrame>
  );
}

function RasterVideoResultLayer({
  layer,
  transport,
  onStatusChange,
}: {
  readonly layer: VideoRasterLayer;
  readonly transport: PlaybackTransport;
  readonly onStatusChange: (layerId: string, status: LayerStatus) => void;
}) {
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const playbackRate = useVideoTimeline(transport, (snapshot) => snapshot.playbackRate);
  const { media } = layer.source;
  const handleStatusChange = useCallback(
    (status: VideoStatus) => onStatusChange(layer.id, status === "error" ? "error" : "idle"),
    [layer.id, onStatusChange],
  );
  return (
    <RasterLayerFrame
      $alignment={normalizeAlignment(layer.alignment)}
      $role={layer.role}
      data-media-result-layer={layer.id}
      data-media-result-raster={layer.role}
      data-raster-interpretation={layer.interpretation.type}
    >
      <RasterVideoSurface
        src={media.src}
        poster={media.poster}
        crossOrigin={media.crossOrigin}
        controls={false}
        showLabel={false}
        playing={playing}
        currentTimeSeconds={layerTime(currentTimeSeconds, layer.timing)}
        playbackRate={playbackRate}
        aria-label=""
        onStatusChange={handleStatusChange}
      />
    </RasterLayerFrame>
  );
}

type RegisteringPlaybackTransport = PlaybackTransport & {
  registerVideo(channelId: string): (element: HTMLVideoElement | null) => void;
};

function isRegisteringPlaybackTransport(
  transport: PlaybackTransport,
): transport is RegisteringPlaybackTransport {
  return "registerVideo" in transport && typeof transport.registerVideo === "function";
}

function ResultBaseVideo({
  binding,
  onStatusChange,
  onRasterChange,
}: {
  readonly binding: AnnotationMediaResultBinding;
  readonly onStatusChange: (status: VideoStatus) => void;
  readonly onRasterChange: RasterReporter;
}) {
  const { source, transport } = binding;
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const playbackRate = useVideoTimeline(transport, (snapshot) => snapshot.playbackRate);
  const registerVideo = useMemo(
    () =>
      isRegisteringPlaybackTransport(transport)
        ? transport.registerVideo(source.baseMedia.id)
        : undefined,
    [source.baseMedia.id, transport],
  );
  const setRasterVideo = useVideoRaster(onRasterChange);
  const setVideoRef = useCallback(
    (element: VideoHandle | null) => {
      registerVideo?.(element);
      setRasterVideo(element);
    },
    [registerVideo, setRasterVideo],
  );
  const externallyRegistered = registerVideo !== undefined;

  return (
    <CapturedVideoSurface
      ref={setVideoRef}
      src={source.baseMedia.src}
      poster={source.baseMedia.poster}
      crossOrigin={source.baseMedia.crossOrigin}
      controls={false}
      showLabel={false}
      playing={externallyRegistered ? undefined : playing}
      currentTimeSeconds={externallyRegistered ? undefined : currentTimeSeconds}
      playbackRate={externallyRegistered ? undefined : playbackRate}
      timeUpdateIntervalMs={100}
      aria-label=""
      onStatusChange={onStatusChange}
    />
  );
}

export function MediaResultPlayer({
  binding,
  targets,
  locale,
}: {
  readonly binding: AnnotationMediaResultBinding;
  readonly targets: readonly AnnotationTarget[];
  readonly locale: AnnotationStudioLocale;
}) {
  const [layerStatuses, setLayerStatuses] = useState<Readonly<Record<string, LayerStatus>>>({});
  const [baseMediaStatus, setBaseMediaStatus] = useState<Readonly<{
    identity: string;
    error: boolean;
  }> | null>(null);
  const baseMediaIdentity = JSON.stringify([
    binding.source.id,
    binding.source.revision,
    binding.source.baseMedia.src,
  ]);
  const targetsById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets],
  );
  const setLayerStatus = useCallback((layerId: string, status: LayerStatus) => {
    setLayerStatuses((current) =>
      current[layerId] === status ? current : { ...current, [layerId]: status },
    );
  }, []);
  const setBaseStatus = useCallback(
    (status: VideoStatus) =>
      setBaseMediaStatus((current) => {
        const next = { identity: baseMediaIdentity, error: status === "error" };
        return current?.identity === next.identity && current.error === next.error ? current : next;
      }),
    [baseMediaIdentity],
  );
  useEffect(() => {
    const currentLayerIds = new Set(binding.source.layers.map((layer) => layer.id));
    setLayerStatuses((current) => {
      const nextEntries = Object.entries(current).filter(([layerId]) =>
        currentLayerIds.has(layerId),
      );
      return nextEntries.length === Object.keys(current).length
        ? current
        : Object.fromEntries(nextEntries);
    });
  }, [binding.source.layers]);
  const stage = useAnnotationRasterStage({
    hasRaster: Boolean(binding.source.baseMedia.src),
    declaredAspectRatio: binding.source.baseMedia.aspectRatio,
  });
  const stageProps = annotationStageProps(stage);
  // layer가 base video의 크기와 첫 프레임을 기다리는 동안도 overlay 대기다. layer가 없는 합성 결과는 기다릴 게 없다.
  const pending =
    (stage.overlayPending && binding.source.layers.length > 0) ||
    Object.values(layerStatuses).some((status) => status === "pending");
  const error =
    (baseMediaStatus?.identity === baseMediaIdentity && baseMediaStatus.error) ||
    Object.values(layerStatuses).some((status) => status === "error");
  // 결과를 열거나 카메라를 바꿀 때마다 생기는 짧은 대기에 깜빡이지 않게 늦게 보인다. 오류는 곧바로 보인다.
  const pendingVisible = useDelayedFlag(pending, BUFFERING_INDICATOR_DELAY_MS);

  return (
    <AnnotationViewport
      role="img"
      aria-label={annotationStudioText("mediaResultViewport", locale)}
      data-media-result-player=""
      data-media-result-id={binding.source.id}
      data-media-result-revision={String(binding.source.revision)}
      {...stageProps}
      style={{ ...stageProps.style, "--c6-annotation-cursor": "default" } as CSSProperties}
    >
      <Raster>
        {binding.source.baseMedia.src ? (
          <ResultBaseVideo
            binding={binding}
            onStatusChange={setBaseStatus}
            onRasterChange={stage.reportRaster}
          />
        ) : (
          <EmptyRaster />
        )}
        <AnnotationOverlay visible={stage.overlayVisible}>
          {binding.source.layers.map((layer) =>
            layer.kind === "contour" ? (
              <ContourResultLayer
                key={layer.id}
                layer={layer}
                transport={binding.transport}
                targetsById={targetsById}
                onStatusChange={setLayerStatus}
              />
            ) : layer.source.delivery === "samples" ? (
              <SampledRasterResultLayer
                key={layer.id}
                layer={layer as SampledRasterLayer}
                transport={binding.transport}
                onStatusChange={setLayerStatus}
              />
            ) : (
              <RasterVideoResultLayer
                key={layer.id}
                layer={layer as VideoRasterLayer}
                transport={binding.transport}
                onStatusChange={setLayerStatus}
              />
            ),
          )}
        </AnnotationOverlay>
      </Raster>
      {pendingVisible || error ? (
        <ViewportOverlayActions>
          <ResultStatus
            $error={error}
            role={error ? "alert" : "status"}
            aria-live={error ? "assertive" : "polite"}
          >
            {error ? null : <Spinner size="sm" />}
            {annotationStudioText(
              error ? "mediaResultLayerLoadFailed" : "mediaResultLoading",
              locale,
            )}
          </ResultStatus>
        </ViewportOverlayActions>
      ) : null}
    </AnnotationViewport>
  );
}
